// Must not import React: the on-screen picker and the PDF renderer share this
// geometry so the printed diagram matches what the doctor placed on screen.

//  Geometry - two straight rows of anatomical crowns (buccal view)

// FDI order along each arch, patient's right → left as drawn.
export const UPPER_FDI: readonly number[] = [
    18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28
];
export const LOWER_FDI: readonly number[] = [
    48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38
];

// Padding beyond the crowns (y≈1.6..256.7) keeps the number labels drawn outside
// the gum ends from clipping and clears the "Upper/Lower arch" corner labels.
export const ODONTOGRAM_VIEWBOX = { minX: 0, minY: -48, width: 1221, height: 388 } as const;
export const TOOTH_RADIUS = 12;

export interface ToothPoint {
    fdi: number;
    x: number;
    y: number;
}

export type ToothCategory = 'incisor' | 'canine' | 'premolar' | 'molar';

// Teeth are stored and laid out internally in FDI notation; this maps each to
// the US Universal Numbering System (1-32) for the numbers shown to the user.
const FDI_TO_UNIVERSAL: Record<number, number> = {
    18: 1,
    17: 2,
    16: 3,
    15: 4,
    14: 5,
    13: 6,
    12: 7,
    11: 8,
    21: 9,
    22: 10,
    23: 11,
    24: 12,
    25: 13,
    26: 14,
    27: 15,
    28: 16,
    38: 17,
    37: 18,
    36: 19,
    35: 20,
    34: 21,
    33: 22,
    32: 23,
    31: 24,
    41: 25,
    42: 26,
    43: 27,
    44: 28,
    45: 29,
    46: 30,
    47: 31,
    48: 32
};

/** Display label for a tooth: US Universal number (1-32), stored FDI otherwise. */
export function toothLabel(fdi: number): string {
    const us = FDI_TO_UNIVERSAL[fdi];
    return us ? String(us) : String(fdi);
}

/** Derive the anatomical tooth type from the last FDI digit (1..8). */
export function toothCategory(fdi: number): ToothCategory {
    const pos = fdi % 10;
    if (pos <= 2) return 'incisor';
    if (pos === 3) return 'canine';
    if (pos <= 5) return 'premolar';
    return 'molar';
}

// Each crown carries its center and top/bottom Y so anchors and labels derive
// without parsing the path; upper teeth have the gum end at the top (small Y).
const TOOTH_GEOMETRY: Record<
    number,
    { d: string; cx: number; cy: number; topY: number; botY: number }
> = {
    18: {
        d: 'M18.3946 102.011C29.5946 103.611 39.728 94.8447 44.3946 90.5114C49.8946 97.5114 69.8946 107.253 77.8946 102.011C92.3946 92.5114 91.1049 58.1422 89.3946 53.0114C87.8946 48.5114 82.3946 38.0114 73.8946 32.0114C66.3946 25.5114 51.3946 20.4114 35.3946 24.0114C15.3946 28.5114 5.84291 42.5114 4.39463 49.5114C1.39462 64.0114 1.85617 67.0114 2.39463 74.0114C3.0613 82.678 7.19464 100.411 18.3946 102.011Z',
        cx: 46.9,
        cy: 63.8,
        topY: 20.4,
        botY: 107.3
    },
    17: {
        d: 'M113.395 105.011C122.195 105.811 130.395 98.3447 133.395 94.5114C135.728 105.178 158.741 108.622 163.395 107.011C176.395 102.511 179.395 87.5114 179.895 71.0114C180.51 50.6993 167.228 37.8447 158.895 32.5114C152.395 28.3514 136.095 20.0114 116.895 30.0114C92.8946 42.5114 91.8946 57.5114 91.3946 77.0114C90.8946 96.5114 102.395 104.011 113.395 105.011Z',
        cx: 135.7,
        cy: 64.3,
        topY: 20.0,
        botY: 108.6
    },
    16: {
        d: 'M179.395 89.5114C183.395 109.911 212.728 103.345 226.895 97.5114L236.895 105.011C244.895 112.011 264.195 112.911 267.395 88.5114C271.395 58.0114 256.488 23.6431 229.895 22.5114C206.395 21.5114 174.395 64.0114 179.395 89.5114Z',
        cx: 222.9,
        cy: 67.2,
        topY: 21.5,
        botY: 112.9
    },
    15: {
        d: 'M267.395 79.0114C267.395 44.6114 288.395 32.3447 298.895 30.5114C309.695 29.7114 323.595 46.5114 325.395 55.5114C327.061 63.8447 328.723 83.2554 325.895 99.0114C322.395 118.511 296.895 121.011 294.395 121.011C291.895 121.011 267.395 122.011 267.395 79.0114Z',
        cx: 298.1,
        cy: 75.9,
        topY: 29.7,
        botY: 122.0
    },
    14: {
        d: 'M325.895 82.5114C322.865 42.6183 344.395 25.0114 359.895 26.0114C375.395 27.0114 379.895 36.0114 379.895 36.0114C379.895 36.0114 388.895 47.0114 390.395 77.5114C392.045 111.064 378.061 115.345 371.395 119.011C356.561 123.345 328.895 122.011 325.895 82.5114Z',
        cx: 357.5,
        cy: 74.2,
        topY: 25.0,
        botY: 123.3
    },
    13: {
        d: 'M457.895 73.0114C459.495 36.2114 430.395 19.5114 417.895 20.0114C405.395 20.5114 387.895 36.0114 388.895 80.5114C389.695 116.111 408.895 130.511 422.395 129.511C435.895 128.511 456.295 109.811 457.895 73.0114Z',
        cx: 423.7,
        cy: 75.0,
        topY: 19.5,
        botY: 130.5
    },
    12: {
        d: 'M452.895 78.5114C452.894 34.5114 468.894 21.5114 481.395 20.5114C493.895 19.5114 510.894 31.4114 514.894 75.0114C518.894 118.611 500.894 119.511 482.894 119.511C464.895 119.511 452.895 122.511 452.895 78.5114Z',
        cx: 485.9,
        cy: 71.0,
        topY: 19.5,
        botY: 122.5
    },
    11: {
        d: 'M512.895 81.5114C514.894 19.1114 547.395 2.37402 558.395 2.01139C569.395 1.64876 610.395 9.51139 610.395 81.5114C610.395 131.705 592.395 126.011 560.895 126.011C516.395 126.011 511.677 119.511 512.895 81.5114Z',
        cx: 561.0,
        cy: 66.7,
        topY: 1.6,
        botY: 131.7
    },
    21: {
        d: 'M610.895 81.5228C609.895 21.5114 644.056 2.38546 655.056 2.02283C666.056 1.66021 707.056 9.52283 707.056 81.5228C707.056 131.716 689.056 126.023 657.556 126.023C613.056 126.023 611.528 119.537 610.895 81.5228Z',
        cx: 658.5,
        cy: 66.7,
        topY: 1.7,
        botY: 131.7
    },
    22: {
        d: 'M707.395 78.0685C707.395 34.0685 723.395 21.0685 735.895 20.0685C748.395 19.0685 765.395 30.9685 769.395 74.5685C773.395 118.168 755.395 119.068 737.395 119.068C719.395 119.068 707.395 122.068 707.395 78.0685Z',
        cx: 740.4,
        cy: 70.6,
        topY: 19.1,
        botY: 122.1
    },
    23: {
        d: 'M831.395 76.5114C832.995 113.311 807.895 130.33 799.936 130.011C791.977 129.693 761.895 114.511 762.895 70.0114C763.695 34.4114 785.977 20.0599 799.936 20.0599C813.895 20.0599 829.795 39.7114 831.395 76.5114Z',
        cx: 797.4,
        cy: 75.2,
        topY: 20.1,
        botY: 130.3
    },
    24: {
        d: 'M827.683 85.5516C824.653 45.6585 846.183 28.0516 861.683 29.0516C877.183 30.0516 881.683 39.0516 881.683 39.0516C881.683 39.0516 890.683 50.0516 892.183 80.5516C893.833 114.104 879.849 118.385 873.183 122.052C858.349 126.385 830.683 125.052 827.683 85.5516Z',
        cx: 859.2,
        cy: 77.2,
        topY: 28.1,
        botY: 126.4
    },
    25: {
        d: 'M952.421 76.539C952.421 42.139 931.421 29.8723 920.921 28.039C910.121 27.239 896.221 44.039 894.421 53.039C892.754 61.3723 891.093 80.7829 893.921 96.539C897.421 116.039 922.921 118.539 925.421 118.539C927.921 118.539 952.421 119.539 952.421 76.539Z',
        cx: 921.8,
        cy: 73.4,
        topY: 27.2,
        botY: 119.5
    },
    26: {
        d: 'M1041.04 93.0287C1037.04 113.429 1007.7 106.862 993.538 101.029L983.538 108.529C975.538 115.529 956.238 116.429 953.038 92.0287C949.038 61.5287 963.945 27.1604 990.538 26.0287C1014.04 25.0287 1046.04 67.5287 1041.04 93.0287Z',
        cx: 997.5,
        cy: 70.7,
        topY: 25.0,
        botY: 116.4
    },
    27: {
        d: 'M1107.92 105.737C1099.12 106.537 1090.92 99.0708 1087.92 95.2374C1085.58 105.904 1062.57 109.348 1057.92 107.737C1044.92 103.237 1041.92 88.2374 1041.42 71.7374C1040.8 51.4253 1054.08 38.5708 1062.42 33.2374C1068.92 29.0774 1085.22 20.7374 1104.42 30.7374C1128.42 43.2374 1129.42 58.2374 1129.92 77.7374C1130.42 97.2374 1118.92 104.737 1107.92 105.737Z',
        cx: 1085.6,
        cy: 65.0,
        topY: 20.7,
        botY: 109.3
    },
    28: {
        d: 'M1202.41 105.204C1191.21 106.804 1181.07 98.0369 1176.41 93.7035C1170.91 100.704 1150.91 110.445 1142.91 105.204C1128.41 95.7035 1129.7 61.3343 1131.41 56.2035C1132.91 51.7035 1138.41 41.2035 1146.91 35.2035C1154.41 28.7035 1169.41 23.6035 1185.41 27.2035C1205.41 31.7035 1214.96 45.7035 1216.41 52.7035C1219.41 67.2035 1218.94 70.2035 1218.41 77.2035C1217.74 85.8702 1213.61 103.604 1202.41 105.204Z',
        cx: 1173.9,
        cy: 67.0,
        topY: 23.6,
        botY: 110.4
    },
    48: {
        d: 'M55.3946 167.5C74.1946 138.7 93.5613 153.833 100.895 165C127.295 136.2 148.029 160.5 148.895 175.5C149.395 184.167 147.495 203.2 137.895 214C128.295 224.8 105.061 224.333 91.8946 223C72.3946 221.025 36.5946 196.3 55.3946 167.5Z',
        cx: 93.0,
        cy: 180.5,
        topY: 136.2,
        botY: 224.8
    },
    47: {
        d: 'M154.395 174.5C171.195 146.5 191.061 162.167 198.895 173.5C203.295 164.3 215.395 161.333 220.895 161C242.395 159.697 254.895 191.5 243.395 212C227.538 240.266 197.561 235.333 186.395 230.5C168.728 223.5 137.595 202.5 154.395 174.5Z',
        cx: 196.2,
        cy: 193.4,
        topY: 146.5,
        botY: 240.3
    },
    46: {
        d: 'M245.395 197C247.395 167.5 275.895 150 297.895 181.5C302.728 177.667 307.895 166.5 324.395 166.5C340.895 166.5 344.394 188.5 340.395 214C337.057 235.28 309.394 242.5 293.395 242.5C280.394 242.5 243.378 226.742 245.395 197Z',
        cx: 293.9,
        cy: 196.3,
        topY: 150.0,
        botY: 242.5
    },
    45: {
        d: 'M340.395 203.5C339.595 184.3 368.895 167 375.395 168.5C384.895 168.5 404.095 181.9 400.895 211.5C396.895 248.5 380.895 243 367.395 243C354.618 243 341.395 227.5 340.395 203.5Z',
        cx: 371.8,
        cy: 207.8,
        topY: 167.0,
        botY: 248.5
    },
    44: {
        d: 'M394.895 208C394.895 172 417.228 168.667 429.895 168.5C446.895 168.276 461.895 187.7 459.895 210.5C457.395 239 437.417 245.5 431.895 245.5C428.895 245.5 394.895 248 394.895 208Z',
        cx: 428.4,
        cy: 208.1,
        topY: 168.3,
        botY: 248.0
    },
    43: {
        d: 'M452.895 206C451.444 172.637 463.895 160.667 472.895 158.5C483.395 158.5 489.895 169 489.895 169C489.895 169 504.395 194.5 497.895 224C492.695 247.6 477.228 248.667 469.895 246L469.835 245.978C464.811 244.152 454.389 240.363 452.895 206Z',
        cx: 477.9,
        cy: 203.6,
        topY: 158.5,
        botY: 248.7
    },
    42: {
        d: 'M493.895 200C493.895 171.929 509.795 169.5 523.395 169.5C541.895 169.5 548.228 173.333 550.395 176.5C553.395 182.833 551.395 200 553.895 223C555.948 241.889 538.895 254.5 526.395 255.5C517.568 256.206 508.228 247.667 504.395 242C501.561 239 493.895 229.5 493.895 200Z',
        cx: 524.9,
        cy: 212.9,
        topY: 169.5,
        botY: 256.2
    },
    41: {
        d: 'M551.395 198.5C551.395 170.429 557.895 168 580.895 168C599.395 168 605.728 171.833 607.895 175C610.895 181.333 609.895 198.365 609.895 221.5C609.895 240.5 596.395 254.5 583.895 255.5C575.068 256.206 565.728 246.167 561.895 240.5C558.895 235 551.395 228 551.395 198.5Z',
        cx: 581.1,
        cy: 212.1,
        topY: 168.0,
        botY: 256.2
    },
    31: {
        d: 'M608.395 198.5C608.395 170.429 615.895 169.5 638.895 169.5C657.395 169.5 664.728 171.333 666.895 174.5C669.895 180.833 665.895 198.364 665.895 221.5C665.895 240.5 645.395 255.14 640.895 255.5C632.068 256.206 622.728 246.167 618.895 240.5C615.895 235 608.395 228 608.395 198.5Z',
        cx: 639.1,
        cy: 212.9,
        topY: 169.5,
        botY: 256.2
    },
    32: {
        d: 'M667.395 199.5C668.65 171.457 672.895 170 695.895 170C714.395 170 720.728 171.833 722.895 175C725.895 181.333 722.895 198.864 722.895 222C722.895 241 702.395 255.64 697.895 256C689.068 256.706 678.228 249.167 674.395 243.5C671.395 238 665.895 233 667.395 199.5Z',
        cx: 695.9,
        cy: 213.4,
        topY: 170.0,
        botY: 256.7
    },
    33: {
        d: 'M769.071 206.5C770.521 173.137 758.071 161.167 749.071 159C738.571 159 732.071 169.5 732.071 169.5C732.071 169.5 717.571 195 724.071 224.5C729.271 248.1 744.737 249.167 752.071 246.5L752.131 246.478C757.154 244.652 767.577 240.863 769.071 206.5Z',
        cx: 744.0,
        cy: 204.1,
        topY: 159.0,
        botY: 249.2
    },
    34: {
        d: 'M827.574 207.502C827.574 171.502 805.241 168.169 792.574 168.002C775.574 167.778 760.574 187.202 762.574 210.002C765.074 238.502 785.052 245.002 790.574 245.002C793.574 245.002 827.574 247.502 827.574 207.502Z',
        cx: 794.1,
        cy: 207.6,
        topY: 167.8,
        botY: 247.5
    },
    35: {
        d: 'M882.25 202.091C883.05 182.891 853.75 165.591 847.25 167.091C837.75 167.091 818.55 180.491 821.75 210.091C825.75 247.091 841.75 241.591 855.25 241.591C868.027 241.591 881.25 226.091 882.25 202.091Z',
        cx: 850.8,
        cy: 206.3,
        topY: 165.6,
        botY: 247.1
    },
    36: {
        d: 'M976.903 197.217C974.903 167.717 946.403 150.217 924.403 181.717C919.57 177.883 914.403 166.717 897.903 166.717C881.403 166.717 877.903 188.717 881.903 214.217C885.241 235.497 912.903 242.717 928.903 242.717C941.903 242.717 978.919 226.959 976.903 197.217Z',
        cx: 928.4,
        cy: 196.5,
        topY: 150.2,
        botY: 242.7
    },
    37: {
        d: 'M1067.76 174.729C1050.96 146.729 1031.09 162.396 1023.26 173.729C1018.86 164.529 1006.76 161.563 1001.26 161.229C979.761 159.926 967.261 191.729 978.761 212.229C994.618 240.495 1024.59 235.563 1035.76 230.729C1053.43 223.729 1084.56 202.729 1067.76 174.729Z',
        cx: 1025.9,
        cy: 193.6,
        topY: 146.7,
        botY: 240.5
    },
    38: {
        d: 'M1164.97 167.591C1146.17 138.791 1126.8 153.924 1119.47 165.091C1093.07 136.291 1072.33 160.591 1071.47 175.591C1070.97 184.258 1072.87 203.291 1082.47 214.091C1092.07 224.891 1115.3 224.424 1128.47 223.091C1147.97 221.116 1183.77 196.391 1164.97 167.591Z',
        cx: 1127.4,
        cy: 180.6,
        topY: 136.3,
        botY: 224.9
    }
};

const UPPER_SET = new Set<number>(UPPER_FDI);
const TOOTH_ORDER: readonly number[] = [...UPPER_FDI, ...LOWER_FDI];

// Pushes the hand-authored lower arch down to open a gap between the arches;
// applied to every derived position so the whole diagram stays aligned.
const ARCH_GAP = 34;

/** The screen offset applied to a tooth's hand-authored geometry. Only the
 *  lower arch moves — straight down by ARCH_GAP. */
export function toothOffset(fdi: number): { dx: number; dy: number } {
    return { dx: 0, dy: UPPER_SET.has(fdi) ? 0 : ARCH_GAP };
}

/** All tooth positions (crown centers), upper arch first. */
export function toothLayout(): ToothPoint[] {
    return TOOTH_ORDER.map((fdi) => {
        const g = TOOTH_GEOMETRY[fdi];
        const { dx, dy } = toothOffset(fdi);
        return { fdi, x: g.cx + dx, y: g.cy + dy };
    });
}

export function toothCenter(fdi: number): { x: number; y: number } | undefined {
    const g = TOOTH_GEOMETRY[fdi];
    if (!g) return undefined;
    const { dx, dy } = toothOffset(fdi);
    return { x: g.cx + dx, y: g.cy + dy };
}

/** How far from the outer crown edge the button/anchor sits, as a fraction of
 *  the half-crown height. Small = close to the edge. */
const BUTTON_EDGE_BIAS = 0.35;

/** Where the bond button is drawn and the band attaches: near each crown's
 *  outer edge so buttons sit away from the arch gap. */
export function toothButtonAnchor(fdi: number): { x: number; y: number } {
    const g = TOOTH_GEOMETRY[fdi];
    if (!g) return { x: 0, y: 0 };
    const { dx, dy } = toothOffset(fdi);
    const y = UPPER_SET.has(fdi)
        ? g.topY + (g.cy - g.topY) * BUTTON_EDGE_BIAS
        : g.botY - (g.botY - g.cy) * BUTTON_EDGE_BIAS;
    return { x: g.cx + dx, y: y + dy };
}

/** Number-label position: just outside the gum end (above upper teeth, below
 *  lower teeth). */
export function toothLabelPos(fdi: number): { x: number; y: number } {
    const g = TOOTH_GEOMETRY[fdi];
    if (!g) return { x: 0, y: 0 };
    const { dx, dy } = toothOffset(fdi);
    const y = UPPER_SET.has(fdi) ? g.topY - 16 : g.botY + 16;
    return { x: g.cx + dx, y: y + dy };
}

/** Whether an FDI number belongs to the upper arch (used to split the picker). */
export function isUpperTooth(fdi: number): boolean {
    return UPPER_SET.has(fdi);
}

export interface ToothGlyph {
    category: ToothCategory;
    /** Filled crown outline path (absolute coords, ready for <path d=…>). */
    outline: string;
}

/** The hand-drawn crown outline for a tooth, in the shared 1221x259 viewBox. */
export function toothGlyph(fdi: number): ToothGlyph {
    const g = TOOTH_GEOMETRY[fdi];
    return { category: toothCategory(fdi), outline: g ? g.d : '' };
}
